import { useMemo, useState } from 'react';
import { PartnerEntity } from '../../../../../utils/types/admin_types';

const extractExpandableIds = (entities: PartnerEntity[]): string[] => {
  const ids: string[] = [];
  const traverse = (items: PartnerEntity[]) => {
    items.forEach((item) => {
      if (item.subEntities && item.subEntities.length > 0) {
        ids.push(item.id);
        traverse(item.subEntities);
      }
    });
  };
  traverse(entities);
  return ids;
};

const countAllEntities = (entities: PartnerEntity[]): number => {
  let count = 0;
  const traverse = (items: PartnerEntity[]) => {
    count += items.length;
    items.forEach((item) => {
      if (item.subEntities && item.subEntities.length > 0) {
        traverse(item.subEntities);
      }
    });
  };
  traverse(entities);
  return count;
};

export const useEntityTree = (
  entities: PartnerEntity[] | undefined,
  onDeleteClick: (entity: PartnerEntity) => void,
) => {
  const [expandedEntities, setExpandedEntities] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [entityToDelete, setEntityToDelete] = useState<PartnerEntity | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);

  const safeEntities = useMemo(() => entities || [], [entities]);

  const allExpandableIds = useMemo(
    () => extractExpandableIds(safeEntities),
    [safeEntities],
  );

  const totalEntitiesCount = useMemo(
    () => countAllEntities(safeEntities),
    [safeEntities],
  );

  const isAllExpanded = useMemo(() => {
    if (allExpandableIds.length === 0) return false;
    return allExpandableIds.every((id) => expandedEntities.includes(id));
  }, [allExpandableIds, expandedEntities]);

  const handleToggle = (entityId: string) => {
    setExpandedEntities((prev) =>
      prev.includes(entityId)
        ? prev.filter((id) => id !== entityId)
        : [...prev, entityId],
    );
  };

  const handleToggleExpandAll = () => {
    if (isAllExpanded) {
      setExpandedEntities([]);
    } else {
      setExpandedEntities(allExpandableIds);
    }
  };

  const handleOpenDeleteDialog = (entity: PartnerEntity) => {
    setEntityToDelete(entity);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setEntityToDelete(null);
    setIsDeleteDialogOpen(false);
  };

  const handleConfirmDelete = () => {
    if (entityToDelete) {
      onDeleteClick(entityToDelete);
      handleCloseDeleteDialog();
    }
  };

  // Filter entities according to search query
  const { filteredEntities, searchExpandIds } = useMemo(() => {
    if (!searchQuery.trim()) {
      return { filteredEntities: safeEntities, searchExpandIds: [] };
    }
    const term = searchQuery.toLowerCase().trim();
    const expandIds: string[] = [];

    const filterNode = (node: PartnerEntity): PartnerEntity | null => {
      const isSelfMatch =
        (node.name && node.name.toLowerCase().includes(term)) ||
        (node.alias && node.alias.toLowerCase().includes(term)) ||
        (node.location?.city && node.location.city.toLowerCase().includes(term)) ||
        (node.location?.country && node.location.country.toLowerCase().includes(term));

      const matchingChildren: PartnerEntity[] = [];
      if (node.subEntities && node.subEntities.length > 0) {
        node.subEntities.forEach((child: PartnerEntity) => {
          const match = filterNode(child);
          if (match) {
            matchingChildren.push(match);
          }
        });
      }

      if (isSelfMatch || matchingChildren.length > 0) {
        if (matchingChildren.length > 0) {
          expandIds.push(node.id);
        }
        return {
          ...node,
          subEntities: matchingChildren,
        };
      }
      return null;
    };

    const results = safeEntities
      .map(filterNode)
      .filter((n): n is PartnerEntity => n !== null);

    return { filteredEntities: results, searchExpandIds: expandIds };
  }, [safeEntities, searchQuery]);

  // Combine user expanded with auto-expanded nodes from search
  const effectiveExpanded = useMemo(() => {
    if (searchQuery.trim()) {
      return Array.from(new Set([...expandedEntities, ...searchExpandIds]));
    }
    return expandedEntities;
  }, [expandedEntities, searchExpandIds, searchQuery]);

  return {
    expandedEntities: effectiveExpanded,
    searchQuery,
    setSearchQuery,
    isAllExpanded,
    totalEntitiesCount,
    rootEntitiesCount: filteredEntities.length,
    filteredEntities,
    entityToDelete,
    isDeleteDialogOpen,
    handleToggle,
    handleToggleExpandAll,
    handleOpenDeleteDialog,
    handleCloseDeleteDialog,
    handleConfirmDelete,
  };
};
